import { RouterOutletFade } from "@redotech/react-router-util/outlet-fade";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import DotIcon from "@redotech/redo-icons/dot-01.svg";
import ExternalLinkIcon from "@redotech/redo-icons/link-external-02.svg";
import { PopulatedSplit } from "@redotech/redo-model/split";
import { Team } from "@redotech/redo-model/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoHorizontalTab,
  RedoHorizontalTabs,
} from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { BreadcrumbSlot, useBreadcrumb } from "@redotech/redo-web/breadcrumb";
import { Flex } from "@redotech/redo-web/flex";
import { Action0PortalContext, Actions } from "@redotech/redo-web/page";
import { TabsPortal } from "@redotech/redo-web/tabs-portal";
import { createContext, memo, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  useMatch,
  useNavigate,
  useParams,
  useResolvedPath,
} from "react-router-dom";
import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../client/context";
import { EcommerceProviderIntegrationProvider } from "../client/ecommerce-provider-integration-context";
import { getSplits, getTeam } from "../client/team";
import { useMerchantAppLogin } from "./merchant-app";
import * as teamCss from "./team.module.css";
import { PatchAdminSettingsClientProvider } from "./team/patch-admin-settings-client";

const enum TeamTab {
  AEO = "aeo",
  ANALYTICS = "analytics",
  CONVERSION = "conversion",
  COVERAGE = "coverage",
  CUSTOMER_ACCOUNTS = "customer-accounts",
  CUSTOMER_SUPPORT = "customer-support",
  FEDEX_EASY_RETURNS = "fedex-easy-returns",
  GENERAL = "general",
  IMS = "ims",
  LANDED_COST = "landed-cost",
  MARKETING = "marketing",
  ORDERS = "orders",
  OUTBOUND_LABELS = "outbound-labels",
  POS = "pos",
  RECOMMERCE = "recommerce",
  RETURNS_SETTINGS_V2 = "returns",
  TNPL = "tnpl",
  WARRANTIES = "warranties",
}

const teamTabs: RedoHorizontalTab<TeamTab>[] = [
  { label: "General", key: TeamTab.GENERAL },
  { label: "AEO", key: TeamTab.AEO },
  { label: "Analytics", key: TeamTab.ANALYTICS },
  { label: "Conversion", key: TeamTab.CONVERSION },
  { label: "Cust Accounts", key: TeamTab.CUSTOMER_ACCOUNTS },
  { label: "FedEx Easy Returns", key: TeamTab.FEDEX_EASY_RETURNS },
  { label: "IMS", key: TeamTab.IMS },
  { label: "Landed cost", key: TeamTab.LANDED_COST },
  { label: "Logistics", key: TeamTab.RECOMMERCE },
  { label: "Marketing", key: TeamTab.MARKETING },
  { label: "Orders", key: TeamTab.ORDERS },
  { label: "Outbound Lbls", key: TeamTab.OUTBOUND_LABELS },
  { label: "Pos", key: TeamTab.POS },
  { label: "Returns", key: TeamTab.RETURNS_SETTINGS_V2 },
  { label: "Support & AI", key: TeamTab.CUSTOMER_SUPPORT },
  { label: "Try First", key: TeamTab.TNPL },
  { label: "Warranties", key: TeamTab.WARRANTIES },
];

export const TeamContext = createContext<Team | undefined>(undefined);
export const LoadTeamContext = createContext<(() => void) | undefined>(
  undefined,
);

export const TeamPage = memo(function TeamPage({
  breadcrumb,
}: {
  breadcrumb: BreadcrumbSlot;
}) {
  const client = useRequiredContext(RedoAdminClientContext);
  const adminRpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const path = useResolvedPath("");
  const match = useMatch({ path: `${path.pathname}/:child`, end: false });

  const params = useParams();
  const teamId = params.teamId!;

  const [teamLoad, loadTeam] = useTriggerLoad((signal) =>
    getTeam(client, { teamId, signal }),
  );

  const [splitLoad, loadSplit] = useTriggerLoad((signal) =>
    getSplits(client, { teamId, signal }),
  );

  const blueYonderUpcharge = useLoad(
    () => adminRpcClient.getBlueYonderUpcharge({ teamId }),
    [adminRpcClient, teamId],
  );

  useBreadcrumb(breadcrumb, teamLoad.value?.name || "");

  useEffect(() => {
    loadTeam();
    loadSplit();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const navigate = useNavigate();

  const actions0 = useContext(Action0PortalContext);

  // Filter tabs based on FedEx Easy Returns integration status
  const filteredTeamTabs = teamTabs.filter((tab) => {
    if (tab.key === TeamTab.FEDEX_EASY_RETURNS) {
      return blueYonderUpcharge.value?.hasIntegration === true;
    }
    return true;
  });

  if (!match || !teamLoad.value) {
    return null;
  }

  return (
    <TeamContext.Provider value={teamLoad.value}>
      <EcommerceProviderIntegrationProvider>
        {actions0 &&
          teamLoad.value &&
          createPortal(
            <Actions show>
              <Flex dir="row" gap="md">
                <TreatmentsAction splits={splitLoad.value} />
                <MerchantAppAction team={teamLoad.value} />
              </Flex>
            </Actions>,
            actions0,
          )}

        <div className={teamCss.container}>
          <TabsPortal>
            <RedoHorizontalTabs
              drawBottomLine={false}
              pl="none"
              selectedTab={match.params.child as TeamTab}
              setSelectedTab={(option) => navigate(option)}
              tabs={filteredTeamTabs}
            />
          </TabsPortal>
          <PatchAdminSettingsClientProvider>
            <div className={teamCss.container}>
              <LoadTeamContext.Provider value={loadTeam}>
                <RouterOutletFade />
              </LoadTeamContext.Provider>
            </div>
          </PatchAdminSettingsClientProvider>
        </div>
      </EcommerceProviderIntegrationProvider>
    </TeamContext.Provider>
  );
});

const MerchantAppAction = memo(function MerchantAppAction({
  team,
}: {
  team: Team;
}) {
  const merchantAppLogin = useMerchantAppLogin();

  const handleClick = useHandler(() => {
    merchantAppLogin(team._id);
  });

  return (
    <RedoButton
      hierarchy="secondary"
      IconTrailing={ExternalLinkIcon}
      onClick={handleClick}
      size="sm"
      text="Merchant dashboard"
    />
  );
});

const TreatmentsAction = memo(function TreatmentsAction({
  splits,
}: {
  splits: PopulatedSplit[] | undefined;
}) {
  const navigate = useNavigate();

  const hasActiveSplit = splits?.some((split) => split.active);

  return (
    <RedoButton
      hierarchy="secondary"
      IconLeading={
        hasActiveSplit
          ? () => <DotIcon className={teamCss.splitStatus} />
          : undefined
      }
      onClick={() => navigate("split")}
      size="sm"
      text="A/B testing"
    />
  );
});
